import React, { Component } from 'react';
import { RootState } from '../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { BulkActionState, BulkActionType } from '../../redux/reducers/BulkActionReducer';
import { closeBulkActionDialog } from '../../redux/actions/BulkActionActions';
import { getComponentIcon } from '../../util/GetComponentIcon';
import ComponentName from '../../constant/ComponentName';
import Icon from '../../icons/Icon';
import { AnimatedButton } from '@trlogic/ui-pack';
import LanguageUtil from '../../util/LanguageUtil';
import NavigationUtil from '../../util/NavigationUtil';

class BulkActionConfirmDialog extends Component<Props> {
  public render() {
    const { bulkAction } = this.props;

    if (!bulkAction.isOpen || !bulkAction.payload) {
      return null;
    }

    const { dependencyErrors } = bulkAction.payload;

    if (dependencyErrors && dependencyErrors.length > 0) {
      return this.renderDependencyErrorDialog();
    }

    return this.renderConfirmDialog();
  }

  private renderConfirmDialog = () => {
    const { bulkAction } = this.props;
    const { actionType, componentName, hasMixedStatuses, selectedStatuses } = bulkAction.payload!;

    const title = this.getTitle(actionType, componentName);
    const description = this.getDescription(actionType, selectedStatuses);
    const buttonType = actionType === 'REMOVE' ? 'fraud' : 'primary';
    const cancelButtonType = actionType === 'REMOVE' ? 'outlined' : 'secondary';

    return (
      <div className='fixed inset-0 z-[1100] flex items-center justify-center bg-black/50'>
        <div className='bg-white rounded-lg shadow-xl w-[450px] p-6'>
          <div className='flex flex-col items-start gap-y-2'>
            <h3 className='text-primary font-bold text-[18px] text-center'>{title}</h3>
            {hasMixedStatuses && <div className='text-[#667085] text-[13px]'>{description}</div>}
          </div>
          <div className='flex justify-end items-center gap-x-4 mt-6'>
            <AnimatedButton
              onClick={this.handleCancel}
              icon='close'
              text={LanguageUtil.get({ id: 'common.label.cancel', defaultMessage: 'Cancel' })}
              customType={cancelButtonType}
            />
            <AnimatedButton
              customType={buttonType}
              onClick={this.handleConfirm}
              icon={this.getActionIcon(actionType)}
              text={this.getActionButtonText(actionType)}
            />
          </div>
        </div>
      </div>
    );
  };

  private renderDependencyErrorDialog = () => {
    const { bulkAction } = this.props;
    const { dependencyErrors, componentName } = bulkAction.payload!;

    return (
      <div className='fixed inset-0 z-[1100] flex items-center justify-center bg-black/50'>
        <div className='bg-white rounded-lg shadow-xl w-[500px] p-6'>
          <div className='flex flex-col items-start gap-y-2'>
            {this.renderIcon('REMOVE')}
            <h3 className='text-primary font-bold text-[18px] text-center'>
              {LanguageUtil.get({ id: 'messages.bulkAction.dependencyTitle', defaultMessage: 'Dependency Check Failed' })}
            </h3>
            <p className='text-[#667085] text-[13px]'>
              {LanguageUtil.get({
                id: 'messages.bulkAction.dependencyDescription',
                defaultMessage: 'The following items cannot be deleted because they are used in other components.',
              })}
            </p>
          </div>
          <div className='flex flex-col gap-y-4 py-4 max-h-[300px] overflow-y-auto mt-4'>
            {dependencyErrors!.map((error, index) => (
              <div key={index} className='flex flex-col gap-y-2 border-b border-[#E4E7EC] pb-3'>
                <div className='flex items-center gap-x-2 text-f-black-2 text-[13px] font-semibold'>
                  <Icon icon='error' className='text-red-500' size={14} />
                  {error.id}
                </div>
                <div className='ml-6'>
                  {error.dependencies.map((dep, depIndex) => (
                    <div
                      key={depIndex}
                      className='flex items-center gap-x-2 text-f-black-3 text-[12px] cursor-pointer hover:text-primary'
                      onClick={() => this.onClickComponent(dep.componentType, dep.componentId)}
                    >
                      <Icon icon={getComponentIcon(dep.componentType as ComponentName)} className='text-f-black-3' size={12} />
                      <span>
                        {dep.componentType}: <span className='text-[#458EF7]'>{dep.componentId}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className='flex justify-center items-center mt-4'>
            <AnimatedButton
              onClick={this.handleCancel}
              icon='check'
              text={LanguageUtil.get({ id: 'common.label.okay', defaultMessage: 'Okay' })}
              customType='primary'
            />
          </div>
        </div>
      </div>
    );
  };

  private renderIcon = (actionType: BulkActionType) => {
    const iconName = actionType === 'REMOVE' ? 'delete' : actionType === 'DEPLOY' ? 'deploy' : 'undeploy';
    const bgColor = actionType === 'REMOVE' ? 'bg-red-100' : 'bg-blue-100';
    const iconColor = actionType === 'REMOVE' ? 'text-red-500' : 'text-primary';

    return (
      <div className={`w-16 h-16 rounded-full ${bgColor} flex items-center justify-center`}>
        <Icon icon={iconName} className={iconColor} size={32} />
      </div>
    );
  };

  private getTitle = (actionType: BulkActionType, componentLabel: string): string => {
    switch (actionType) {
      case 'REMOVE':
        return LanguageUtil.get({ id: 'messages.bulkAction.removeTitle', defaultMessage: 'Remove {component}' }).replace('{component}', componentLabel);
      case 'DEPLOY':
        return LanguageUtil.get({ id: 'messages.bulkAction.deployTitle', defaultMessage: 'Deploy {component}' }).replace('{component}', componentLabel);
      case 'UNDEPLOY':
        return LanguageUtil.get({ id: 'messages.bulkAction.undeployTitle', defaultMessage: 'Undeploy {component}' }).replace('{component}', componentLabel);
      default:
        return '';
    }
  };

  private getDescription = (actionType: BulkActionType, selectedStatuses: string[]): React.ReactNode => {
    switch (actionType) {
      case 'REMOVE':
        return LanguageUtil.get({
          id: 'messages.bulkAction.removeDescription',
          defaultMessage: 'Are you sure you want to remove? If you want to delete all of them, only the drafts will be deleted.',
        });
      case 'DEPLOY':
        return LanguageUtil.get({
          id: 'messages.bulkAction.deployDescription',
          defaultMessage: 'Are you sure you want to deploy? If you want to deploy, only the draft ones will be deployed.',
        });
      case 'UNDEPLOY':
        const nonDraftStatuses = selectedStatuses.filter((s) => s !== 'DRAFT');
        const statusesToShow = nonDraftStatuses.length > 0 ? nonDraftStatuses : ['ACTIVE', 'INACTIVE', 'FAILED'];
        return (
          <span>
            {LanguageUtil.get({
              id: 'messages.bulkAction.undeployDescriptionPrefix',
              defaultMessage: 'Are you sure you want to undeploy? If you want to undeploy,',
            })}{' '}
            {statusesToShow.map((status, index) => (
              <span key={status}>
                <span style={{ color: this.getStatusColor(status), fontWeight: 600 }}>{this.formatStatusName(status)}</span>
                {index < statusesToShow.length - 1 && ', '}
              </span>
            ))}{' '}
            {LanguageUtil.get({
              id: 'messages.bulkAction.undeployDescriptionSuffix',
              defaultMessage: 'ones will be undeployed.',
            })}
          </span>
        );
      default:
        return '';
    }
  };

  private getStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      ACTIVE: '#027A48',
      DRAFT: '#5925DC',
      INACTIVE: '#FF7020',
      SCHEDULED: '#3A3A3A',
      FAILED: '#D2332F',
    };
    return colorMap[status.toUpperCase()] || '#667085';
  };

  private formatStatusName = (status: string): string => {
    const statusKey = status.toLowerCase();
    const defaultValue = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    return LanguageUtil.get({ id: `common.status.${statusKey}`, defaultMessage: defaultValue });
  };

  private getActionIcon = (actionType: BulkActionType): string => {
    switch (actionType) {
      case 'REMOVE':
        return 'delete';
      case 'DEPLOY':
        return 'deploy';
      case 'UNDEPLOY':
        return 'undeploy';
      default:
        return 'check';
    }
  };

  private getActionButtonText = (actionType: BulkActionType): string => {
    switch (actionType) {
      case 'REMOVE':
        return LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' });
      case 'DEPLOY':
        return LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' });
      case 'UNDEPLOY':
        return LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' });
      default:
        return LanguageUtil.get({ id: 'common.label.okay', defaultMessage: 'Okay' });
    }
  };

  private handleCancel = () => {
    this.props.closeBulkActionDialog();
  };

  private handleConfirm = () => {
    const { bulkAction, closeBulkActionDialog } = this.props;
    if (bulkAction.payload?.onConfirm) {
      bulkAction.payload.onConfirm();
    }
    closeBulkActionDialog();
  };

  private onClickComponent = (componentType: string, componentId: string) => {
    const tenantName = NavigationUtil.getTenant();
    const url = this.getComponentUrl(componentType, componentId, tenantName);
    if (url) {
      window.open(url, '_blank');
    }
  };

  private getComponentUrl = (componentType: string, componentId: string, tenantName: string): string => {
    const urlMap: Record<string, string> = {
      [ComponentName.RULE]: `/${tenantName}/rule/edit/${componentId}`,
      [ComponentName.TRANSACTION]: `/${tenantName}/transaction/edit/${componentId}`,
      [ComponentName.LIST]: `/${tenantName}/list/edit/${componentId}`,
      [ComponentName.STRUCT]: `/${tenantName}/struct/edit/${componentId}`,
      [ComponentName.ACTION]: `/${tenantName}/action/edit/${componentId}`,
      [ComponentName.STREAM]: `/${tenantName}/stream/edit/${componentId}`,
      [ComponentName.CONNECTION]: `/${tenantName}/connection/edit/${componentId}`,
      [ComponentName.CONFIGURATION]: `/${tenantName}/configuration/edit/${componentId}`,
      [ComponentName.AI_MODEL]: `/${tenantName}/ai-model/edit/${componentId}`,
      [ComponentName.TRACKER]: `/${tenantName}/tracker/edit/${componentId}`,
      [ComponentName.VELOCITY]: `/${tenantName}/enrichment/velocity/${componentId}`,
      [ComponentName.LIST_VARIABLE]: `/${tenantName}/enrichment/list/${componentId}`,
      [ComponentName.QUERY_VARIABLE]: `/${tenantName}/enrichment/query/${componentId}`,
      [ComponentName.BASIC_VARIABLE]: `/${tenantName}/enrichment/basic/${componentId}`,
      [ComponentName.API_VARIABLE]: `/${tenantName}/enrichment/api/${componentId}`,
    };
    return urlMap[componentType] || '';
  };
}

interface Props extends ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  bulkAction: state.bulkAction as BulkActionState,
});

const mapDispatchToProps = { closeBulkActionDialog };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(BulkActionConfirmDialog);
